import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import {
  MarketplaceBody,
  MarketplaceTitle,
} from "@/components/MarketplacePage";
import { MarketplaceSearch } from "@/components/MarketplaceSearch";
import { Spinner } from "@/components/Spinner";
import { TopBlock } from "@/components/TopBlock";
import {
  getMonetizedFeeds,
  getPublicFeeds,
  searchCustomNodesQueryOptions,
  SearchResult,
} from "@/lib/api";

export const Route = createFileRoute("/_marketplace-layout/marketplace")({
  component: RouteComponent,
  loader: async () => {
    const [ads, publicFeeds] = await Promise.all([
      getMonetizedFeeds({
        page: 1,
        page_size: 6,
        sort_by: "popularity",
        search: "",
        days: 30,
      }),
      getPublicFeeds({
        page: 1,
        page_size: 6,
        sort_by: "popularity",
        search: "",
        days: 30,
      }),
    ]);
    return {
      ads: ads.results as Omit<SearchResult, "algorithm">[],
      publicFeeds: publicFeeds.results as Omit<SearchResult, "algorithm">[],
    };
  },
});

function RouteComponent() {
  const { ads, publicFeeds } = Route.useLoaderData();
  const { data: customNodes, isLoading: isLoadingCustomNodes } =
    useInfiniteQuery(
      searchCustomNodesQueryOptions({
        query: "",
        sort_by: "install_count",
        order: "desc",
        page: 1,
        page_size: 12,
      }),
    );

  return (
    <>
      <MarketplaceTitle>Marketplace</MarketplaceTitle>
      <MarketplaceBody>
        <div className="flex flex-col gap-20">
          <div className="flex flex-col">
            <div className="max-w-[600px] w-full mx-auto mb-14 md:mb-20">
              <MarketplaceSearch />
            </div>
            <TopBlock
              title="Ads"
              description="Reach your audience with sponsored content on these feeds hosted on Graze."
              to="/ads"
              tooltip="View All"
              results={ads as SearchResult[]}
            />
          </div>
          <TopBlock
            title="Public Feeds"
            description="See how other feed creators are using Graze by browsing their feed definitions."
            to="/feeds"
            tooltip="View All"
            results={publicFeeds as SearchResult[]}
          />
          <TopBlock
            title="Custom Nodes"
            description="Use custom bits of logic from the community to supercharge your feeds. Custom nodes are created by other users to make creating great feeds easier."
            to="/custom-nodes"
            tooltip="View All"
            results={customNodes?.pages.flatMap((page) => page.results) || []}
          />
          {isLoadingCustomNodes && (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          )}
        </div>
      </MarketplaceBody>
    </>
  );
}
